import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, Gift, Sparkles, Crown, Check, FileText } from "lucide-react";
import { toast } from "sonner";
import IssuerLayout from "@/components/IssuerLayout";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Settings = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [settings, setSettings] = useState({
    organization_name: "",
    email: "",
    is_paid: false,
    certificates_issued: 0,
    certificates_limit: null,
    certificates_remaining: null,
    is_appsumo: false,
    appsumo_tier: 0,
    plan_name: "Free",
    is_admin: false
  });

  useEffect(() => {
    const token = localStorage.getItem('issuer_token');
    if (!token) {
      navigate("/issuer/login");
      return;
    }
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const token = localStorage.getItem('issuer_token');
      const response = await axios.get(`${API}/settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSettings({
        organization_name: response.data.organization_name,
        email: response.data.email,
        is_paid: response.data.is_paid || false,
        certificates_issued: response.data.certificates_issued || 0,
        certificates_limit: response.data.certificates_limit,
        certificates_remaining: response.data.certificates_remaining,
        is_appsumo: response.data.is_appsumo || false,
        appsumo_tier: response.data.appsumo_tier || 0,
        plan_name: response.data.plan_name || "Free",
        is_admin: response.data.is_admin || false
      });
      
      // Update local storage with admin status
      const issuerData = JSON.parse(localStorage.getItem('issuer_data') || '{}');
      issuerData.is_admin = response.data.is_admin || false;
      localStorage.setItem('issuer_data', JSON.stringify(issuerData));
    } catch (err) {
      toast.error("Failed to load settings");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const token = localStorage.getItem('issuer_token');
      await axios.put(`${API}/settings`, {
        organization_name: settings.organization_name
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Settings updated!");
    } catch (err) {
      toast.error("Failed to update settings");
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('issuer_token');
    localStorage.removeItem('issuer_data');
    toast.success("Logged out successfully");
    navigate("/issuer/login");
  };

  return (
    <IssuerLayout>
      <div className="max-w-2xl" data-testid="settings-page">
        <div className="mb-8">
          <h1 className="text-4xl font-medium text-slate-900 mb-2" style={{fontFamily: 'Playfair Display'}}>Settings</h1>
          <p className="text-slate-600">Manage your account settings</p>
        </div>

        {/* Account Information */}
        <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-8 mb-6">
          <h2 className="text-xl font-semibold text-slate-900 mb-4" style={{fontFamily: 'Playfair Display'}}>Account Information</h2>
          <div className="space-y-4">
            <div className="flex items-center justify-between py-3 border-b border-slate-100">
              <span className="text-slate-600">Account Tier</span>
              <span className="font-semibold text-slate-900">
                {settings.is_appsumo ? (
                  <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-gradient-to-r from-amber-100 to-orange-100 text-amber-800 border border-amber-200">
                    <Gift className="h-3.5 w-3.5" />
                    {settings.plan_name}
                  </span>
                ) : settings.is_paid ? (
                  <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-green-100 text-green-800">
                    <Crown className="h-3.5 w-3.5" />
                    Pro
                  </span>
                ) : (
                  <span className="inline-flex items-center px-3 py-1 rounded-full bg-slate-100 text-slate-800">
                    Free
                  </span>
                )}
              </span>
            </div>
            
            {/* Certificate Usage */}
            <div className="flex items-center justify-between py-3 border-b border-slate-100">
              <span className="text-slate-600">Certificates Issued</span>
              <span className="font-semibold text-slate-900">
                {settings.certificates_limit === null ? (
                  <span>{settings.certificates_issued} <span className="text-green-600 text-sm font-normal">(Unlimited)</span></span>
                ) : (
                  <span>{settings.certificates_issued} / {settings.certificates_limit}</span>
                )}
              </span>
            </div>
            
            {/* Progress bar for limited plans */}
            {settings.certificates_limit !== null && (
              <div className="py-2">
                <div className="w-full bg-slate-100 rounded-full h-2.5">
                  <div 
                    className={`h-2.5 rounded-full ${
                      settings.is_appsumo 
                        ? 'bg-gradient-to-r from-amber-500 to-orange-500' 
                        : 'bg-slate-700'
                    }`}
                    style={{ width: `${Math.min(100, (settings.certificates_issued / settings.certificates_limit) * 100)}%` }}
                  />
                </div>
                <p className="text-xs text-slate-500 mt-1.5">
                  {settings.certificates_remaining} certificates remaining
                </p>
              </div>
            )}
            
            {/* AppSumo Account Details */}
            {settings.is_appsumo && (
              <div className="bg-gradient-to-br from-amber-50 to-orange-50 border border-amber-200 rounded-lg p-4 mt-2">
                <div className="flex items-start gap-3">
                  <div className="p-2 bg-gradient-to-br from-amber-500 to-orange-500 rounded-lg">
                    <Sparkles className="h-5 w-5 text-white" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold text-amber-900 mb-1">AppSumo Lifetime Deal</h3>
                    <p className="text-sm text-amber-800 mb-3">
                      You're on {settings.plan_name}. 
                      {settings.appsumo_tier < 3 && " Stack codes to upgrade your tier!"}
                    </p>
                    {settings.appsumo_tier < 3 ? (
                      <Button
                        onClick={() => navigate('/appsumo')}
                        size="sm"
                        className="bg-gradient-to-r from-amber-600 to-orange-600 hover:from-amber-700 hover:to-orange-700 text-white rounded-lg"
                        data-testid="redeem-another-code-btn"
                      >
                        <Gift className="h-4 w-4 mr-1.5" />
                        Redeem Another Code
                      </Button>
                    ) : (
                      <div className="flex items-center gap-2 text-green-700 text-sm">
                        <Check className="h-4 w-4" />
                        <span>Maximum tier reached - Unlimited certificates!</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
            
            {/* Free Tier Upgrade Prompt */}
            {!settings.is_paid && !settings.is_appsumo && (
              <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 mt-2">
                <p className="text-sm text-slate-600 mb-3">
                  Upgrade to Pro for unlimited certificates and premium features
                </p>
                <div className="flex gap-2">
                  <Button
                    onClick={() => navigate('/pricing')}
                    className="bg-slate-900 text-white hover:bg-slate-800 rounded-lg px-6 py-2 font-medium"
                    data-testid="upgrade-btn"
                  >
                    Upgrade Now
                  </Button>
                  <Button
                    onClick={() => navigate('/appsumo')}
                    variant="outline"
                    className="border-amber-300 text-amber-700 hover:bg-amber-50 rounded-lg px-6 py-2 font-medium"
                    data-testid="have-appsumo-code-btn"
                  >
                    <Gift className="h-4 w-4 mr-1.5" />
                    Have AppSumo Code?
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Organization Settings */}
        <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-8 mb-6">
          <h2 className="text-xl font-semibold text-slate-900 mb-4" style={{fontFamily: 'Playfair Display'}}>Organization Settings</h2>
          <form onSubmit={handleSubmit} className="space-y-6" data-testid="settings-form">
            <div>
              <Label htmlFor="org" className="text-sm font-medium text-slate-700 mb-1.5 block">Organization Name</Label>
              <Input
                id="org"
                type="text"
                value={settings.organization_name}
                onChange={(e) => setSettings({...settings, organization_name: e.target.value})}
                placeholder="Your Organization"
                required
                className="h-11 rounded-lg border-slate-200"
                data-testid="org-input"
              />
              <p className="text-xs text-slate-500 mt-1">This name will appear on your certificates</p>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="bg-slate-900 text-white hover:bg-slate-800 rounded-lg px-6 py-3 font-medium"
              data-testid="save-settings-btn"
            >
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save Changes"}
            </Button>
          </form>
        </div>

        {/* Admin Section */}
        {settings.is_admin && (
          <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-8 mb-6">
            <h2 className="text-xl font-semibold text-slate-900 mb-4" style={{fontFamily: 'Playfair Display'}}>Admin Tools</h2>
            <div className="space-y-4">
              <div className="flex items-center justify-between py-3">
                <div>
                  <p className="font-medium text-slate-900">Reissued Certificates</p>
                  <p className="text-sm text-slate-500">View all certificates issued via the reissue portal</p>
                </div>
                <Button
                  onClick={() => navigate('/issuer/admin/reissued')}
                  variant="outline"
                  className="border-slate-200"
                  data-testid="admin-reissued-btn"
                >
                  <FileText className="h-4 w-4 mr-2" />
                  View Certificates
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Account Actions */}
        <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-8">
          <h2 className="text-xl font-semibold text-slate-900 mb-4" style={{fontFamily: 'Playfair Display'}}>Account Actions</h2>
          <Button
            onClick={handleLogout}
            variant="outline"
            className="bg-white text-red-600 border border-red-200 hover:bg-red-50 rounded-lg px-6 py-3"
            data-testid="logout-btn"
          >
            Logout
          </Button>
        </div>
      </div>
    </IssuerLayout>
  );
};

export default Settings;
